/**
 * SG — AGM Exempted with Audited Financial Statements
 *
 * 4 documents:
 *  1. Directors' Resolutions in Writing for AGM
 *  2. Members' Resolutions for AGM (Multiple)
 *  3. Members' Resolutions for AGM (Sole Corporate)
 *  4. Members' Resolutions for AGM (Sole Individual)
 */
export interface MemberRow {
  name: string;
  address: string;
  contactCd?: string;
}

export interface RetiringDirectorRow {
  name: string;
}

export interface SignerRow {
  name: string;
}

export interface AgmEntityPersonRow {
  name: string;
  contactRegNo?: string;
  contactCd?: string;
  email?: string;
  role?: 'director' | 'shareholder';
}

export interface AgmExemptedAfsFormModel {
  companyName: string;
  companyRegistrationNo: string;

  financialYearEndDate: string;
  annualReturnYear: string;
  deemedAgmDate: string;
  resolutionDate: string;

  hasDirectorsRemuneration: boolean;
  auditorName: string;
  hasRetiringDirectors: boolean;

  memberType: 'multiple' | 'sole_corporate' | 'sole_individual';
  members: MemberRow[];
  memberName: string;
  memberRepresentativeName: string;

  directorCount: number;
  specifySigners: boolean;
  signers: SignerRow[];
  retiringDirectors: RetiringDirectorRow[];

  entityDirectors: AgmEntityPersonRow[];
  entityMembers: AgmEntityPersonRow[];
}

export function createEmptyAgmExemptedAfsForm(): AgmExemptedAfsFormModel {
  return {
    companyName: '',
    companyRegistrationNo: '',
    financialYearEndDate: '',
    annualReturnYear: '',
    deemedAgmDate: '',
    resolutionDate: '',
    hasDirectorsRemuneration: false,
    auditorName: '',
    hasRetiringDirectors: false,
    memberType: 'multiple',
    members: [],
    memberName: '',
    memberRepresentativeName: '',
    directorCount: 1,
    specifySigners: false,
    signers: [],
    retiringDirectors: [],
    entityDirectors: [],
    entityMembers: [],
  };
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

export function toAgmExemptedAfsTemplateVariables(m: AgmExemptedAfsFormModel): Record<string, any> {
  const isMultiple = m.memberType === 'multiple';
  const isSoleCorporate = m.memberType === 'sole_corporate';
  const isSoleIndividual = m.memberType === 'sole_individual';

  return {
    company_name: trim(m.companyName),
    company_registration_no: trim(m.companyRegistrationNo),
    financial_year_end_date: trim(m.financialYearEndDate),
    annual_return_year: trim(m.annualReturnYear),
    deemed_agm_date: trim(m.deemedAgmDate),
    resolution_date: trim(m.resolutionDate),
    has_directors_remuneration: m.hasDirectorsRemuneration,
    auditor_name: trim(m.auditorName),
    has_retiring_directors: m.hasRetiringDirectors,
    is_multiple_members: isMultiple,
    is_sole_corporate_member: isSoleCorporate,
    is_sole_individual_member: isSoleIndividual,
    member_name: isSoleCorporate || isSoleIndividual ? trim(m.memberName) : '',
    member_representative_name: isSoleCorporate ? trim(m.memberRepresentativeName) : '',
    director_count: m.directorCount,
    'director_count == 1': m.directorCount === 1,
    'director_count == 2': m.directorCount === 2,
    specify_signers: m.specifySigners,
    retiring_directors: toAgmExemptedAfsRetiringDirectorsRepeatingData(m),
    agm_exempted_afs: true,
  };
}

export function toAgmExemptedAfsRetiringDirectorsRepeatingData(
  m: AgmExemptedAfsFormModel,
): Array<Record<string, string>> {
  return (m.retiringDirectors || [])
    .map((r) => {
      const name = trim(r.name);
      return { 'director.name': name, director_name: name };
    })
    .filter((row) => row['director.name']);
}

export function toAgmExemptedAfsDirectorsResolutionOnlyVariables(
  m: AgmExemptedAfsFormModel,
): Record<string, any> {
  const signers_list = (m.signers || []).map((s) => ({
    signer_name: trim(s.name),
  }));
  return {
    designated_signers: signers_list,
    signers_list,
    retiring_directors: toAgmExemptedAfsRetiringDirectorsRepeatingData(m),
  };
}

export function buildAgmExemptedAfsTaskFormData(
  m: AgmExemptedAfsFormModel,
  _entityCd: string,
): Record<string, any> {
  return {
    agmPresetKind: 'exempted_afs',
    companyName: trim(m.companyName),
    companyRegistrationNo: trim(m.companyRegistrationNo),
    financialYearEndDate: trim(m.financialYearEndDate),
    annualReturnYear: trim(m.annualReturnYear),
    deemedAgmDate: trim(m.deemedAgmDate),
    resolutionDate: trim(m.resolutionDate),
    memberType: m.memberType,
    memberName: trim(m.memberName),
    memberRepresentativeName: trim(m.memberRepresentativeName),
    hasDirectorsRemuneration: m.hasDirectorsRemuneration,
    auditorName: trim(m.auditorName),
    hasRetiringDirectors: m.hasRetiringDirectors,
    directorCount: m.directorCount,
    specifySigners: m.specifySigners,
    signers: (m.signers || []).map((s) => ({ name: trim(s.name) })),
    retiringDirectors: (m.retiringDirectors || []).map((r) => ({ name: trim(r.name) })),
    members: (m.members || []).map((r) => ({
      name: trim(r.name),
      address: trim(r.address),
      contactCd: trim(r.contactCd),
    })),
  };
}

export function isAgmExemptedAfsFormComplete(m: AgmExemptedAfsFormModel): boolean {
  const base = [
    m.companyName,
    m.companyRegistrationNo,
    m.financialYearEndDate,
    m.annualReturnYear,
    m.deemedAgmDate,
    m.resolutionDate,
  ].every((v) => trim(v).length > 0);

  if (!base) return false;

  if (m.memberType === 'multiple') {
    if (m.members.length < 2) return false;
    if (!m.members.every((r) => trim(r.name))) return false;
  } else if (m.memberType === 'sole_corporate') {
    if (!trim(m.memberName) || !trim(m.memberRepresentativeName)) return false;
  } else if (m.memberType === 'sole_individual') {
    if (!trim(m.memberName)) return false;
  }

  if (m.hasRetiringDirectors) {
    if (m.retiringDirectors.length === 0) return false;
    if (!m.retiringDirectors.every((r) => trim(r.name))) return false;
  }
  if (m.hasDirectorsRemuneration && !trim(m.auditorName)) return false;
  if (m.specifySigners && !m.signers.some((s) => trim(s.name))) return false;

  return true;
}
